import React from "react";
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AddOutlined from "@mui/icons-material/AddOutlined";
import AudiotrackOutlined from "@mui/icons-material/AudiotrackOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import VideoLibraryOutlined from "@mui/icons-material/VideoLibraryOutlined";
import VideocamOutlined from "@mui/icons-material/VideocamOutlined";
import { GlobalContext } from "../../../context/GlobalContext";
import { describeCharacterImage } from "../api/describeImage";
import PickImageStudioModal from "./PickImageStudioModal";
import PickVideoLibraryModal from "./PickVideoLibraryModal";
import CharacterImageTile from "./CharacterImageTile";

const DEFAULT_IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";
const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;

const TILE_WIDTH = 104;
const TILE_HEIGHT = 128;

function tileShellSx(theme, { dragOver, disabled }) {
  return {
    width: TILE_WIDTH,
    height: TILE_HEIGHT,
    borderRadius: 2.5,
    border: "1px dashed",
    borderColor: dragOver ? "primary.main" : alpha(theme.palette.text.primary, 0.22),
    bgcolor: dragOver
      ? alpha(theme.palette.primary.main, 0.06)
      : alpha(theme.palette.action.hover, 0.03),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    flexShrink: 0,
    position: "relative",
    overflow: "hidden",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "border-color 0.15s, background-color 0.15s",
    "&:hover": disabled
      ? {}
      : {
          borderColor: alpha(theme.palette.text.primary, 0.4),
          bgcolor: alpha(theme.palette.action.hover, 0.06),
        },
  };
}

function AddTile({ disabled, dragOver, onClick, lang, onDragOver, onDragLeave, onDrop }) {
  const theme = useTheme();
  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick?.(e);
        }
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={tileShellSx(theme, { dragOver, disabled })}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: "1px solid",
          borderColor: alpha(theme.palette.text.primary, 0.28),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AddOutlined sx={{ fontSize: 20, color: "text.secondary" }} />
      </Box>
      <Typography variant="caption" color="text.secondary" fontWeight={500}>
        {lang?.add || "Add"}
      </Typography>
    </Box>
  );
}

function FileTile({ file, kind, disabled, onRemove, lang }) {
  const theme = useTheme();
  const name = file.file?.name || file.name || lang?.videoStudioUploadFile || "File";
  const preview = file.previewUrl || "";
  const showImage = kind === "image" && preview;
  const showVideo = kind === "video" && preview;

  return (
    <Box sx={{ ...tileShellSx(theme, { dragOver: false, disabled: true }), cursor: "default", p: 0.75 }}>
      {!disabled && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={lang?.remove || "Remove"}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            zIndex: 2,
            width: 24,
            height: 24,
            bgcolor: alpha(theme.palette.background.paper, 0.92),
            "&:hover": { bgcolor: "background.paper" },
          }}
        >
          <CloseOutlined sx={{ fontSize: 14 }} />
        </IconButton>
      )}

      {showImage ? (
        <Box
          component="img"
          src={preview}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 2 }}
        />
      ) : showVideo ? (
        <Box
          component="video"
          src={preview}
          muted
          playsInline
          preload="metadata"
          sx={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 2 }}
        />
      ) : (
        <Stack alignItems="center" justifyContent="center" spacing={0.75} sx={{ px: 0.5, textAlign: "center" }}>
          {kind === "video" ? (
            <VideocamOutlined sx={{ fontSize: 28, color: "text.secondary" }} />
          ) : (
            <AudiotrackOutlined sx={{ fontSize: 28, color: "text.secondary" }} />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, wordBreak: "break-word" }}>
            {name.length > 28 ? `${name.slice(0, 25)}…` : name}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

/**
 * @param {{
 *   files?: Array<{ id: string; file?: File; previewUrl: string; name?: string }>;
 *   onChange: (files: any[]) => void;
 *   maxFiles?: number;
 *   label?: string;
 *   hint?: string;
 *   accept?: string;
 *   maxBytes?: number;
 *   disabled?: boolean;
 *   disabledReason?: string;
 *   kind?: 'image'|'video'|'audio';
 *   lang?: object;
 *   enableLibrary?: boolean;
 *   enableVoiceClip?: boolean;
 * }} props
 */
export default function UploadArea({
  files = [],
  onChange,
  maxFiles = 9,
  label,
  hint,
  accept = DEFAULT_IMAGE_ACCEPT,
  maxBytes = DEFAULT_MAX_BYTES,
  disabled = false,
  disabledReason,
  kind = "image",
  lang,
  enableLibrary,
  enableVoiceClip = false,
}) {
  const theme = useTheme();
  const { hitAxios } = React.useContext(GlobalContext);
  const inputRef = React.useRef(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [imageLibOpen, setImageLibOpen] = React.useState(false);
  const [videoLibOpen, setVideoLibOpen] = React.useState(false);
  const describeInFlight = React.useRef(new Set());
  const filesRef = React.useRef(files);
  React.useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const libraryEnabled =
    enableLibrary !== false && (kind === "image" || kind === "video");

  const enqueueDescribe = React.useCallback(
    (item) => {
      if (kind !== "image" || !hitAxios || !item?.id) return;
      if (item.visualDescription || describeInFlight.current.has(item.id)) return;
      const file = item.file instanceof File ? item.file : null;
      const imageUrl =
        !file && item.previewUrl && !String(item.previewUrl).startsWith("blob:")
          ? String(item.previewUrl)
          : "";
      if (!file && !imageUrl) return;
      describeInFlight.current.add(item.id);
      void describeCharacterImage(hitAxios, {
        file: file || undefined,
        imageUrl: imageUrl || undefined,
        label: item.characterName || item.name,
      })
        .then((desc) => {
          if (!desc?.identity) return;
          const latest = filesRef.current || [];
          onChange(
            latest.map((f) =>
              f.id === item.id
                ? {
                    ...f,
                    visualDescription: desc.identity,
                    ...(desc.label_hint && !f.characterName
                      ? { characterName: String(desc.label_hint).slice(0, 28) }
                      : {}),
                  }
                : f,
            ),
          );
        })
        .finally(() => {
          describeInFlight.current.delete(item.id);
        });
    },
    [kind, hitAxios, onChange],
  );

  // Auto-describe new character images so @-tags can lock likeness in the prompt.
  React.useEffect(() => {
    if (kind !== "image") return;
    for (const item of files || []) {
      if (!item?.visualDescription) enqueueDescribe(item);
    }
  }, [kind, files, enqueueDescribe]);

  const validateFile = (file) => {
    if (file.size > maxBytes) return false;
    if (kind === "image") return /^image\/(jpeg|png|webp)$/i.test(file.type);
    if (kind === "video") return /^video\/(mp4|webm|quicktime)$/i.test(file.type);
    if (kind === "audio") return /^audio\/(mpeg|wav|ogg|mp4|x-m4a)$/i.test(file.type);
    return true;
  };

  const addFiles = (fileList) => {
    if (disabled) return;
    const incoming = Array.from(fileList || []);
    const next = [...files];
    for (const file of incoming) {
      if (next.length >= maxFiles) break;
      if (!validateFile(file)) continue;
      next.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl:
          kind === "image" && file
            ? URL.createObjectURL(file)
            : kind === "video" && file
              ? URL.createObjectURL(file)
              : kind === "audio" && file
                ? URL.createObjectURL(file)
                : "",
        name: file.name,
        // Default @tag / character identity from the filename stem (Amara.jpg → Amara).
        characterName: String(file.name || "")
          .replace(/\.[a-z0-9]+$/i, "")
          .trim()
          .slice(0, 28),
      });
    }
    onChange(next);
  };

  const addLibraryItems = (items) => {
    if (disabled || !items?.length) return;
    const next = [...files];
    for (const item of items) {
      if (next.length >= maxFiles) break;
      const previewUrl = String(item.previewUrl || "").trim();
      if (!previewUrl) continue;
      next.push({
        id: `lib-${item.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        previewUrl,
        name: item.name || (kind === "video" ? "Library video" : "Library image"),
        characterName: String(item.name || "")
          .replace(/\.[a-z0-9]+$/i, "")
          .trim()
          .slice(0, 28),
        fromLibrary: true,
        libraryId: item.id,
      });
    }
    onChange(next);
  };

  const removeFile = (id) => {
    const removed = files.find((f) => f.id === id);
    if (removed?.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(removed.previewUrl);
    }
    if (removed?.voiceClip?.previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(removed.voiceClip.previewUrl);
    }
    onChange(files.filter((f) => f.id !== id));
  };

  const updateFile = (id, updated) => {
    onChange(files.map((f) => (f.id === id ? updated : f)));
  };

  React.useEffect(
    () => () => {
      files.forEach((f) => {
        if (f.previewUrl?.startsWith("blob:")) URL.revokeObjectURL(f.previewUrl);
        if (f.voiceClip?.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(f.voiceClip.previewUrl);
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const canAdd = !disabled && files.length < maxFiles;
  const slotsLeft = Math.max(0, maxFiles - files.length);

  const bindDrag = (active) => ({
    onDragOver: (e) => {
      if (!canAdd) return;
      e.preventDefault();
      setDragOver(active);
    },
    onDragLeave: () => setDragOver(false),
    onDrop: (e) => {
      if (!canAdd) return;
      e.preventDefault();
      setDragOver(false);
      addFiles(e.dataTransfer.files);
    },
  });

  const openAddMenu = (e) => {
    if (!canAdd) return;
    if (libraryEnabled) {
      setMenuAnchor(e.currentTarget);
      return;
    }
    inputRef.current?.click();
  };

  const closeMenu = () => setMenuAnchor(null);

  return (
    <Box sx={{ opacity: disabled ? 0.55 : 1, pointerEvents: disabled ? "none" : "auto" }}>
      {label && (
        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}
      {disabled && disabledReason && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {disabledReason}
        </Typography>
      )}

      <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="flex-start">
        {files.map((f) =>
          kind === "image" && enableVoiceClip ? (
            <CharacterImageTile
              key={f.id}
              image={f}
              disabled={disabled}
              lang={lang}
              onChange={(updated) => updateFile(f.id, updated)}
              onRemove={() => removeFile(f.id)}
            />
          ) : (
            <FileTile
              key={f.id}
              file={f}
              kind={kind}
              disabled={disabled}
              lang={lang}
              onRemove={() => removeFile(f.id)}
            />
          ),
        )}
        {canAdd && (
          <AddTile
            disabled={disabled}
            dragOver={dragOver}
            lang={lang}
            onClick={openAddMenu}
            {...bindDrag(true)}
          />
        )}
      </Stack>

      {hint && (
        <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1, lineHeight: 1.4 }}>
          {hint}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={maxFiles > 1}
        hidden
        disabled={disabled}
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {kind === "image" ? (
          <MenuItem
            onClick={() => {
              closeMenu();
              setImageLibOpen(true);
            }}
          >
            <ListItemIcon>
              <ImageOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={lang?.videoStudioPickFromImageStudio || "From Image Studio"}
              secondary={lang?.videoStudioPickFromImageStudioHint || "Choose a generated image"}
            />
          </MenuItem>
        ) : null}
        {kind === "video" ? (
          <MenuItem
            onClick={() => {
              closeMenu();
              setVideoLibOpen(true);
            }}
          >
            <ListItemIcon>
              <VideoLibraryOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={lang?.videoStudioPickFromVideoLibrary || "From Video Library"}
              secondary={lang?.videoStudioPickFromVideoLibraryHint || "Choose an existing video"}
            />
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={() => {
            closeMenu();
            inputRef.current?.click();
          }}
        >
          <ListItemIcon>
            <CloudUploadOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={lang?.videoStudioUploadFromDevice || "Upload from device"}
            secondary={lang?.videoStudioUploadFromDeviceHint || "Choose a file from your computer"}
          />
        </MenuItem>
      </Menu>

      {kind === "image" ? (
        <PickImageStudioModal
          open={imageLibOpen}
          onClose={() => setImageLibOpen(false)}
          maxSelect={slotsLeft}
          lang={lang}
          onConfirm={addLibraryItems}
        />
      ) : null}

      {kind === "video" ? (
        <PickVideoLibraryModal
          open={videoLibOpen}
          onClose={() => setVideoLibOpen(false)}
          maxSelect={slotsLeft}
          lang={lang}
          onConfirm={addLibraryItems}
        />
      ) : null}
    </Box>
  );
}

export function MediaAddTile({ onClick, lang, dragOver, disabled, ...dragHandlers }) {
  return (
    <AddTile
      disabled={disabled}
      dragOver={dragOver}
      lang={lang}
      onClick={onClick}
      {...dragHandlers}
    />
  );
}
